import { useShowSnackbar } from "@/app/hooks/useSnackbar";
import { useDispatch, useSelector } from "react-redux";
import { addStoreCartItem, updateStoreCartItem } from "../redux/cart-management/cart-management-slice";

import cartService from "../redux/cart-management/cart-management-service";
import { throttle } from "lodash";
export default function useCart() {
  const dispatch = useDispatch();
  const { showCartSnackbar } = useShowSnackbar();

  const { items: storeCartItems, subTotal: subTotalPrice } = useSelector(state => state.cart.cartDetails);

  const userEmail = useSelector(state => state?.auth?.userLoginDetails?.email);

  const cartInputValue = useSelector(state => state?.cart?.cartInputValue);

  const isUserLoggedIn = Boolean(userEmail);

  /**
   * Updates the quantity of a specific product in the cart.
   * Dispatches an action to update the store and synchronizes with the server.
   *
   * @async
   * @function updateCartItem
   * @param {Object} options - Options for updating the cart item
   * @param {string} options.productId - Unique identifier of the product to update
   * @param {number} options.quantity - New quantity for the specified product
   * @returns {Promise<void>} A promise that resolves when the update is completed
   */
  async function updateCartItem({ productId, quantity }) {
    if (!productId) return;

    dispatch(updateStoreCartItem({ productId, quantity }));

    if (!isUserLoggedIn) return;
    try {
      const response = await cartService.updateCartDetails({ productId, quantity });
      return response;
    } catch (error) {
      console.error(error);
      console.error("Something went wrong while updating the cart item");
    }
  }

  /**
   * Add a product to the cart.
   * @param {Object} options - Add to cart options
   * @param {Object} options.productDetails - Product details
   * @param {number} options.quantity - Quantity of the product
   * @returns {Promise<void>} Promise that resolves when the cart is updated
   */
  const throttledAddCartDetails = throttle(
    async ({ productId, quantity }) => {
      try {
        await cartService.addCartDetails({ productId, quantity });
      } catch (error) {
        console.error(error);
        console.error("Something went wrong while adding the cart item");
      }
    },
    2000, // Throttle interval in milliseconds
    { trailing: false } // Disable trailing edge calls
  );

  async function addCartItem({ productDetails, quantity, inputQuantity, localCartItems }) {
    if (!productDetails?.id) return;
    if (productDetails?.stockLeft < inputQuantity) return;
    dispatch(addStoreCartItem({ newProduct: productDetails, quantity, inputQuantity, localCartItems }));
    showCartSnackbar(productDetails, inputQuantity, cartInputValue);

    if (!isUserLoggedIn) return;
    try {
      throttledAddCartDetails({
        productId: productDetails.id,
        quantity: inputQuantity, // Assuming a quantity of 1 for the API call
      });
    } catch (error) {
      console.error(error);
      console.error("Something went wrong while adding the cart item");
    }
  }

  async function handleCheckoutCart(payload) {
    try {
      const response = await cartService.cartCheckout(payload);
      return response;
    } catch (error) {
      console.error("Something went wrong while adding the cart item");
    }
  }

  return {
    products: storeCartItems,
    updateCartItem,
    addCartItem,
    handleCheckoutCart,
    subTotalPrice,
  };
}
